import React, { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import getUrgencyLabels from 'lib/offer/getUrgencyLabels'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import OfferUrgencyCountdownLabel from './OfferUrgencyCountdownLabel'
import LineAwardIcon from 'components/Luxkit/Icons/line/LineAwardIcon'

type UrgencyLabelVariant = React.ComponentProps<typeof UrgencyLabel>['variant']

const urgencyLabelTypeToVariant: Record<App.UrgencyLabelType, UrgencyLabelVariant> = {
  critical: 'warning',
  fast: 'speed',
  last_minute_hotel_travel_in: 'critical1',
  left: 'critical2',
  limited: 'warning',
  new: 'positive',
  popular: 'positive',
  lux_plus_general_release: 'warning',
}

interface Props {
  offer?: App.Offer | App.OfferSummary;
  urgencyLabels?: Array<App.OfferUrgencyLabel>;
  className?: string;
  showLimitedAvailability?: boolean;
  countDownShowHourOnly?: boolean,
}

const manuallyHandled = new Set(['left', 'popular', 'lux_plus_general_release'])

const OfferUrgencyLabels = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { offer, urgencyLabels, className, showLimitedAvailability, countDownShowHourOnly = false } = props
  const { currentRegionCode } = useContext(GeoContext)

  const labels = useMemo(() => {
    return urgencyLabels ?? (offer && getUrgencyLabels(offer, currentRegionCode, showLimitedAvailability)) ?? []
  }, [offer, currentRegionCode, urgencyLabels, showLimitedAvailability])

  return <>
    {!!labels.length && <LabelGroup ref={ref} className={className}>
      {labels.map(label => <React.Fragment key={label.type}>
        {label.type === 'left' && label.end && <OfferUrgencyCountdownLabel endDate={label.end} data-testid="UrgencyLabel" showHourOnly={countDownShowHourOnly} />}
        {label.type === 'popular' && label.message && <PromotionLabel variant="default" icon={<LineAwardIcon />} data-testid="UrgencyLabel">{label.message}</PromotionLabel>}
        {label.type === 'lux_plus_general_release' && label.end && <OfferUrgencyCountdownLabel endDate={label.end} data-testid="UrgencyLabel" showHourOnly={countDownShowHourOnly} isLuxPlusExclusive />}
        {!manuallyHandled.has(label.type) && <UrgencyLabel
          data-testid="UrgencyLabel"
          variant={urgencyLabelTypeToVariant[label.type]}
        >
          {label.message}
        </UrgencyLabel>}
      </React.Fragment>)}
    </LabelGroup>}
  </>
})

OfferUrgencyLabels.displayName = 'OfferUrgencyLabels'

export default OfferUrgencyLabels
